import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { concat, forkJoin, Observable, of } from 'rxjs';
import { v4 } from 'uuid';
import { ActionsEnum as AuthActionsEnum } from '../auth-data/auth-data.actions';
import {
  ActionsEnum,
  ChangeEmail,
  changeEmailError,
  changeEmailSuccess,
  FetchAccountDetails,
  fetchAccountDetails,
  FetchAccountDetailsError,
  fetchAccountDetailsError,
  FetchAccountDetailsSuccess,
  fetchAccountDetailsSuccess,
  UpdateAccount,
  updateAccount,
  UpdateUser,
  updateUser,
} from './account-data.actions';
import { AccountDataService } from './account-data.service';
import { Account } from './account.model';
import { User } from './user.model';
import { catchError, flatMap, map, switchMap, take } from 'rxjs/operators';

@Injectable()
export class AccountDataEffects {
  constructor(
    private _actions: Actions,
    private _accountDataService: AccountDataService
  ) {}

  @Effect()
  onAuthentication: Observable<FetchAccountDetails> = this._actions.pipe(
    ofType(AuthActionsEnum.AUTHENTICATED),
    take(1),
    map(() => fetchAccountDetails(v4()))
  );

  @Effect()
  onAuthenticationThroughBilling: Observable<
    UpdateAccount | UpdateUser
  > = this._actions.pipe(
    ofType(AuthActionsEnum.AUTHENTICATED_THROUGH_BILLING),
    take(1),
    flatMap(() => concat([updateAccount(null), updateUser(null)]))
  );

  @Effect()
  getAccountDetails: Observable<
    | UpdateAccount
    | UpdateUser
    | FetchAccountDetailsSuccess
    | FetchAccountDetailsError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_ACCOUNT_DETAILS),
    switchMap(({ payload }: FetchAccountDetails) => {
      const { dispatchId } = payload;

      return forkJoin(
        this._accountDataService.getAccount(),
        this._accountDataService.getUser()
      ).pipe(
        flatMap(([account, user]: (Account | User)[]) =>
          concat([
            updateAccount(account as Account),
            updateUser(user as User),
            fetchAccountDetailsSuccess(dispatchId),
          ])
        ),
        catchError((error) =>
          concat([
            updateAccount(null),
            updateUser(null),
            fetchAccountDetailsError(dispatchId, error),
          ])
        )
      );
    })
  );

  @Effect()
  changeEmail: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.CHANGE_EMAIL),
    switchMap(({ payload }: ChangeEmail) => {
      const { dispatchId } = payload;

      return this._accountDataService
        .changeEmail(payload.emailAddress, payload.locale)
        .pipe(
          map(() => changeEmailSuccess(dispatchId)),
          catchError((error) => of(changeEmailError(dispatchId, error)))
        );
    })
  );
}
